import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  SvgIcon,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import _ from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import DeleteDialog from './dialog/deleteDialog';
import InformIndividualDialog from './dialog/informedIndividualDialog';
import { ButtonAction, genericStyles } from './dialog/styled-components';
import {
  BoxWrapper,
  ButtonWrapper,
  EllipsisWrapper,
} from './styled-components';

import { blueGradientButton } from '../../../../../../assets/styles/blueGradientButton.style';
import { useToastNotificationContext } from '../../../../../../hooks/global/alert/useToastNotification';
import informedIndividualsService from '../../../../../../services/informedIndividualsService';
import {
  setCount,
  setItems,
  setLoading,
  setNext,
  setPage,
  setPrevious,
} from '../../../../../../slices/informedIndividualsSlice';
import { RootState } from '../../../../../../utils/store';
import StackPagination from '../../../../../common/stackPagination';
import AddIcon from '../../../../../icons/addIcon';
import TrashIcon from '../../../../../icons/trashIcon';
import tableStyle from '../styles/tableStyle';

const useTableStyles = makeStyles(tableStyle);

type ItemType = {
  id: number;
  report: string;
  name: string;
  associated_department: number;
  notification_reason: string;
  date_informed_at: string;
  is_deleted: boolean;
  has_error?: boolean;
  errors?: {
    name: boolean;
    associated_department: boolean;
    notification_reason: boolean;
    date_informed: boolean;
  };
};

type DeleteItemType = {
  id: number;
  name: string;
};

const InformedIndividualsTab = () => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const [isSaving, setSaving] = useState<boolean>(false);
  const [isDeleting, setDeleting] = useState<boolean>(false);
  const reportId = useSelector(
    (state: RootState) => state.reports.currentReport,
  );
  const currentPage = useSelector(
    (state: RootState) => state.informedIndividuals.page,
  );
  const count = useSelector(
    (state: RootState) => state.informedIndividuals.count,
  );
  const previousPage = useSelector(
    (state: RootState) => state.informedIndividuals.previous,
  );
  const nextPage = useSelector(
    (state: RootState) => state.informedIndividuals.next,
  );

  

  //const items = useSelector((state: RootState) => state.informedIndividuals.items);
  const [initialItems, setInitialItems] = useState<Array<ItemType>>([]);
  const [tempItems, setTempItems] = useState<Array<ItemType>>([]);
  const departments = useSelector(
    (state: RootState) => state.reports.departments,
  );
  const itemsLoading = useSelector(
    (state: RootState) => state.informedIndividuals.loading,
  );
  const tableClasses = useTableStyles();
  const [informedIndividualDialogOpen, setInformedIndividualDialog] =
    useState<boolean>(false);
  const [isEditMode, setEditMode] = useState<boolean>(false);
  const [isDeleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);

  const [currentDeleteItem, setCurrentDeleteItem] =
    useState<DeleteItemType | null>(null);
  const { createToastNotification } = useToastNotificationContext();

  const handleOpenDialog = () => {
    setInformedIndividualDialog(true);
  };

  const handleCloseDialog = () => {
    setInformedIndividualDialog(false);
  };

  const fetchInformedIndividuals = useCallback(() => {
    dispatch(setLoading(true));
    informedIndividualsService
      .fetchInformedIndividuals(currentPage, reportId)
      .then((response) => {
        dispatch(setItems(response.data.results));
        dispatch(setCount(response.data.count));
        dispatch(setNext(response.data.next));
        dispatch(setPrevious(response.data.previous));
        const getResults: Array<ItemType> = [];

        if (response.data.results.length > 0) {
          response.data.results.map((result) => {
            getResults.push({
              ...result,
              errors: {
                name: false,
                associated_department: false,
                notification_reason: false,
                date_informed: false,
              },
            });
          });
          setInitialItems(getResults);
        } else {
          if (response.data.results.length == 0) {
            setInitialItems([]);
            setEditMode(false);
          }
        }

        dispatch(setLoading(false));
      });
  }, [dispatch, currentPage, reportId]);

  const tableColumns: string[] = [
    intl.formatMessage({ defaultMessage: 'Name' }),
    intl.formatMessage({ defaultMessage: 'Associated Department' }),
    intl.formatMessage({ defaultMessage: 'Notification Reason' }),
    intl.formatMessage({ defaultMessage: 'Date Informed' }),
  ];

  const getDepartmentName = (id: number): string => {
    let departmentName = '';
    const findDepartment = _.find(departments, (e) => e.value == id);
    if (typeof findDepartment != 'undefined')
      departmentName = findDepartment.label;

    return departmentName;
  };

  const toggleEditMode = () => {
    setEditMode(!isEditMode);
  };

  const checkFormErrors = (items: typeof initialItems) => {
    let hasError: boolean = false;

    if (items.length > 0) {
      const checkNameError = _.find(items, (e) => e.errors?.name);
      const checkNotificationReasonError = _.find(
        items,
        (e) => e.errors?.notification_reason,
      );

      if (checkNameError || checkNotificationReasonError) hasError = true;
    }
    return hasError;
  };

  const save = () => {
    if (isEditMode && !isSaving) {
      if (!checkFormErrors(initialItems)) {
        setSaving(true);
        informedIndividualsService
          .patchInformedIndividuals(initialItems)
          .then(() => {
            fetchInformedIndividuals();
            toggleEditMode();
            createToastNotification(
              intl.formatMessage({
                defaultMessage: 'Changes successfully saved.',
              }),
              'success',
            );
            setSaving(false);
          })
          .catch(() => {
            setSaving(false);
            createToastNotification(
              intl.formatMessage({
                defaultMessage: 'An error occurred while trying to edit.',
              }),
              'error',
            );
          });
      }
    } else {
      const getResults: Array<ItemType> = [];
      if (initialItems.length > 0) {
        initialItems.map((result) => {
          getResults.push({
            ...result,
            errors: {
              name: false,
              associated_department: false,
              notification_reason: false,
              date_informed: false,
            },
          });
        });
        setTempItems(getResults);
      }

      toggleEditMode();
    }
  };

  useEffect(() => {
    if (reportId != '') fetchInformedIndividuals();
  }, [reportId, fetchInformedIndividuals]);

  const handlePageChange = async (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ): Promise<void> => {
    dispatch(setLoading(true));
    dispatch(setPage(newPage));
    //dispatch(setSpecialPage(newPage));
    //dispatch(setPage(newPage + 1))

    const url = newPage > currentPage ? nextPage : previousPage;
    informedIndividualsService
      .fetchInformedIndividuals(currentPage, reportId, url)
      .then((response) => {
        dispatch(setItems(response.data.results));
        dispatch(setCount(response.data.count));
        dispatch(setNext(response.data.next));
        dispatch(setPrevious(response.data.previous));

        const getResults: Array<ItemType> = [];

        if (response.data.results.length > 0) {
          response.data.results.map((result) => {
            getResults.push({
              ...result,
              errors: {
                name: false,
                associated_department: false,
                notification_reason: false,
                date_informed: false,
              },
            });
          });
          setInitialItems(getResults);
        } else {
          setInitialItems([]);
          setEditMode(false);
        }

        dispatch(setLoading(false));
      })
      .catch(() => {
        dispatch(setLoading(false));
      });
  };

  return (
    <Box sx={{ pl: 2.5, pr: 2.5, pb: 2.5 }}>
      <Stack
        sx={{
          width: '100%',
        }}
        direction='column'
      >
        <Stack
          sx={{ width: '100%', marginTop: '16px' }}
          justifyContent='space-between'
          direction='row'
        >
          <Stack direction='row' alignItems='center'>
            <Box
              onClick={handleOpenDialog}
              sx={{
                display: 'flex',
                justifyContent: 'flex-start',
                cursor: 'pointer',
                marginTop: '-2px',
              }}
            >
              <AddIcon color='#6D6D6D' width='20' height='15' />
            </Box>
            <BoxWrapper
              onClick={handleOpenDialog}
              sx={{ paddingLeft: '8px', cursor: 'pointer' }}
            >
              <FormattedMessage defaultMessage={'Add Individual'} />
            </BoxWrapper>
          </Stack>
          <Stack direction='row'>
            {isEditMode && initialItems.length > 0 && (
              <Button
                onClick={() => {
                  setInitialItems(tempItems);
                  toggleEditMode();
                }}
                sx={{
                  ...genericStyles,
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: 'transparent !important',
                  },
                  color: '#878787',
                }}
              >
                <FormattedMessage defaultMessage={'Cancel'} />
              </Button>
            )}
            {initialItems.length > 0 && (
              <ButtonWrapper onClick={save}>
                {isSaving && <CircularProgress size={20} />}
                {!isSaving && (
                  <>
                    {isEditMode
                      ? intl.formatMessage({ defaultMessage: 'Save' })
                      : intl.formatMessage({ defaultMessage: 'Edit' })}
                  </>
                )}
              </ButtonWrapper>
            )}
          </Stack>
        </Stack>
        <Divider orientation='vertical' flexItem />
        <Table sx={{ margin: 0 }}>
          <TableHead
            className={tableClasses.tableHead}
            sx={{
              '& th': {
                border: isEditMode ? '0px' : '1px',
              },
            }}
          >
            <TableRow
              sx={{
                borderBottom: `1px solid ${isEditMode ? '#2449FC' : '#808080'}`,
                borderBottomWidth: isEditMode ? '2px' : '1px',
              }}
            >
              {tableColumns.map((tableColumn, keySecond) => {
                return (
                  <TableCell
                    key={`table-cell-${keySecond}`}
                    sx={[{ color: 'grey' }]}
                  >
                    <span>{tableColumn}</span>
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody
            className={tableClasses.tableBody}
            sx={{
              '& .MuiTableRow-root': {
                borderBottom: `1px solid ${isEditMode ? '#2449FC' : '#808080'}`,
                borderBottomWidth: isEditMode ? '2px' : '1px',
              },
            }}
          >
            {!itemsLoading &&
              initialItems.length > 0 &&
              initialItems.map((item, index) => (
                <TableRow key={`table-row-${index}`}>
                  <TableCell sx={{ color: '#3D3D3D', fontSize: '1rem' }}>
                    {!isEditMode && (
                      <Tooltip title={item.name}>
                        <EllipsisWrapper>{item.name}</EllipsisWrapper>
                      </Tooltip>
                    )}
                    {isEditMode && (
                      <TextField
                        fullWidth
                        hiddenLabel
                        sx={{
                          '& .MuiInput-input:focus': {
                            borderBottom: '1px solid #3D3D3D',
                          },
                          '& .MuiInput-input:autofill': {
                            backgroundColor: 'transparent !important',
                          },
                          '& .MuiInput-input': {
                            borderBottom: '1px solid #D6D6D6',
                          },
                          width: '85%',
                        }}
                        className={item.errors?.name ? 'errorClass' : ''}
                        variant='standard'
                        value={item.name}
                        onChange={(e) => {
                          const value = e.target.value;
                          const updatedItems = [...initialItems];

                          const { errors } = updatedItems[index];

                          if (typeof errors?.name != 'undefined')
                            errors.name = value == '' ? true : false;

                          updatedItems[index] = {
                            ...updatedItems[index],
                            name: value,
                            errors,
                          };
                          setInitialItems(updatedItems);
                        }}
                        InputProps={{
                          style: {
                            overflow: 'hidden',
                            border: '',
                          },
                          disableUnderline: true,
                        }}
                      />
                    )}
                  </TableCell>
                  <TableCell sx={{ color: '#3D3D3D', fontSize: '1rem' }}>
                    {!isEditMode &&
                      getDepartmentName(item.associated_department)}
                    {isEditMode && (
                      <FormControl variant='standard' sx={{ width: '98%' }}>
                        <InputLabel
                          id={`departmentId-${item.id}`}
                          sx={{ marginTop: '8px', p: 0 }}
                        >
                          <FormattedMessage defaultMessage={'Department'} />
                        </InputLabel>
                        <Select
                          labelId={`departmentId-${item.id}`}
                          sx={{
                            '& .MuiInput-input:focus': {
                              backgroundColor: 'transparent !important',
                            },
                            width: '85%',
                            '& .MuiInput-input': {
                              borderBottom: '1px solid #D6D6D6',
                            },
                          }}
                          onChange={(e) => {
                            let value = e.target.value;
                            value =
                              typeof value == 'string'
                                ? parseInt(value)
                                : value;
                            const updatedItems = [...initialItems];
                            updatedItems[index] = {
                              ...updatedItems[index],
                              associated_department: value,
                            };
                            setInitialItems(updatedItems);
                          }}
                          value={item.associated_department}
                          IconComponent={() => (
                            <ExpandMoreIcon
                              sx={{
                                width: '30px',
                                height: '30px',
                              }}
                            />
                          )}
                          label={intl.formatMessage({
                            defaultMessage: 'Department',
                          })}
                        >
                          <MenuItem value=''>
                            <em>
                              <FormattedMessage
                                defaultMessage={'Select Department'}
                              />
                            </em>
                          </MenuItem>
                          {departments.length > 0 &&
                            departments.map((department, dkey) => {
                              return (
                                <MenuItem
                                  key={`mi-${dkey}`}
                                  value={department.value}
                                >
                                  {department.label}
                                </MenuItem>
                              );
                            })}
                        </Select>
                      </FormControl>
                    )}
                  </TableCell>
                  <TableCell sx={{ fontSize: '1rem' }}>
                    {!isEditMode && (
                      <Tooltip title={item.notification_reason}>
                        <EllipsisWrapper>
                          {item.notification_reason}
                        </EllipsisWrapper>
                      </Tooltip>
                    )}
                    {isEditMode && (
                      <TextField
                        fullWidth
                        hiddenLabel
                        sx={{
                          width: '85%',
                          '& .MuiInput-input:focus': {
                            borderBottom: '1px solid #3D3D3D',
                          },
                          '& .MuiInput-input': {
                            borderBottom: '1px solid #D6D6D6',
                          },
                        }}
                        className={
                          item.errors?.notification_reason ? 'errorClass' : ''
                        }
                        variant='standard'
                        value={item.notification_reason}
                        onChange={(e) => {
                          const value = e.target.value;
                          const updatedItems = [...initialItems];

                          const { errors } = updatedItems[index];

                          if (typeof errors?.notification_reason != 'undefined')
                            errors.notification_reason =
                              value == '' ? true : false;

                          updatedItems[index] = {
                            ...updatedItems[index],
                            notification_reason: value,
                            errors,
                          };
                          setInitialItems(updatedItems);
                        }}
                        InputProps={{
                          style: {
                            overflow: 'hidden',
                            border: '',
                          },
                          disableUnderline: true,
                        }}
                      />
                    )}
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: '1rem',
                    }}
                  >
                    {!isEditMode && dayjs(item.date_informed_at).format('LL')}
                    {isEditMode && (
                      <Stack direction='row' alignItems='center'>
                        <Box sx={{ mr: '10px' }}>
                          <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DesktopDatePicker
                              label={intl.formatMessage({
                                defaultMessage: 'Date Informed At',
                              })}
                              sx={{
                                width: '98%',
                                marginTop: '0px',
                                '& .MuiInputBase-root': {
                                  marginTop: '0px !important',
                                  borderBottom:
                                    '1px solid var(--ri-input-border-color) !important',
                                },
                                '& .MuiInputLabel-root': {
                                  display: 'none !important',
                                },
                              }}
                              value={
                                item.date_informed_at == ''
                                  ? null
                                  : dayjs(item.date_informed_at)
                              }
                              onChange={(value) => {
                                let newValue: string = '';
                                if (typeof value != 'string')
                                  newValue =
                                    value != null ? value?.toString() : '';
                                else newValue = value;

                                const updatedItems = [...initialItems];
                                updatedItems[index] = {
                                  ...updatedItems[index],
                                  date_informed_at: newValue,
                                };
                                setInitialItems(updatedItems);
                              }}
                              slotProps={{
                                textField: {
                                  error: false,
                                  variant: 'standard',
                                  InputProps: {
                                    disableUnderline: true,
                                  },
                                },
                              }}
                            />
                          </LocalizationProvider>
                        </Box>
                        <SvgIcon fontSize='small' sx={{ display: 'none' }}>
                          <svg
                            width='14'
                            height='20'
                            viewBox='0 0 14 20'
                            fill='none'
                            xmlns='http://www.w3.org/2000/svg'
                          >
                            <path
                              d='M12.8307 5.0013V16.668C12.8307 17.11 12.6551 17.5339 12.3426 17.8465C12.03 18.159 11.6061 18.3346 11.1641 18.3346H2.83073C2.3887 18.3346 1.96478 18.159 1.65222 17.8465C1.33966 17.5339 1.16406 17.11 1.16406 16.668V5.0013M3.66406 5.0013V3.33464C3.66406 2.89261 3.83966 2.46868 4.15222 2.15612C4.46478 1.84356 4.8887 1.66797 5.33073 1.66797H8.66406C9.10609 1.66797 9.53001 1.84356 9.84257 2.15612C10.1551 2.46868 10.3307 2.89261 10.3307 3.33464V5.0013'
                              stroke='#FF5E56'
                              strokeWidth='1.6'
                              strokeLinecap='round'
                              strokeLinejoin='round'
                            />
                          </svg>
                        </SvgIcon>
                        <Button
                          onClick={() => {
                            setCurrentDeleteItem({
                              id: item.id,
                              name: item.name,
                            });
                            setDeleteDialogOpen(true);
                          }}
                          sx={{
                            justifyContent: 'flex-start',
                            '&:hover': {
                              backgroundColor: 'transparent !important',
                            },
                          }}
                        >
                          <TrashIcon color='#FF5E56' width='20' height='20' />
                        </Button>
                      </Stack>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            {itemsLoading && (
              <TableRow sx={{ width: '100%' }}>
                <TableCell
                  colSpan={tableColumns.length}
                  sx={{ width: '100%', textAlign: 'center' }}
                >
                  <CircularProgress size={20} />
                </TableCell>
              </TableRow>
            )}
            {!itemsLoading && initialItems.length == 0 && (
              <TableRow sx={{ width: '100%' }}>
                <TableCell
                  colSpan={tableColumns.length}
                  sx={{ width: '100%', textAlign: 'center' }}
                >
                  <FormattedMessage
                    defaultMessage={'No Informed Individuals found'}
                  />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        {count > 25 && (
          <StackPagination
            count={count}
            currentPage={currentPage}
            onPageChange={handlePageChange}
          />
        )}
        <DeleteDialog
          open={isDeleteDialogOpen}
          dialogTitle={intl.formatMessage({
            defaultMessage: 'Delete Informed Individual',
          })}
          handleClose={() => {
            setDeleteDialogOpen(false);
          }}
          content={
            <Box sx={{ py: 2, minHeight: '120px' }}>
              <Stack direction='row'>
                <Box>
                  <FormattedMessage
                    defaultMessage={
                      'Please confirm, this action is irreversible.'
                    }
                  />
                </Box>
              </Stack>
            </Box>
          }
          footer={
            <>
              <ButtonAction
                onClick={() => {
                  setDeleteDialogOpen(false);
                }}
                sx={{ color: '#878787' }}
              >
                <FormattedMessage defaultMessage='Cancel' />
              </ButtonAction>
              <Button
                onClick={() => {
                  if (!isDeleting && currentDeleteItem != null) {
                    setDeleting(true);
                    informedIndividualsService
                      .deleteInformedIndividual(currentDeleteItem.id)
                      .then(() => {
                        createToastNotification(
                          intl.formatMessage({
                            defaultMessage: 'Record was successfully deleted.',
                          }),
                          'success',
                        );
                        setDeleting(false);
                        fetchInformedIndividuals();

                        setTimeout(() => {
                          setDeleteDialogOpen(false);
                        }, 3000);
                      })
                      .catch(() => {
                        createToastNotification(
                          intl.formatMessage({
                            defaultMessage:
                              'An error occurred while trying to delete a record.',
                          }),
                          'error',
                        );
                        setDeleting(false);
                      });
                  }
                }}
                sx={{
                  ...blueGradientButton,
                  width: '97px',
                  height: '38px',
                  padding: '14px 24px',
                  fontStyle: 'normal',
                  fontWeight: 600,
                  '&:hover': {
                    backgroundImage: 'transparent !important',
                  },
                  textTransform: 'none',
                }}
              >
                {isDeleting && <CircularProgress size={20} />}
                {!isDeleting && <FormattedMessage defaultMessage={'Delete'} />}
              </Button>
            </>
          }
        />
        <InformIndividualDialog
          open={informedIndividualDialogOpen}
          handleClose={handleCloseDialog}
          fetchInformedIndividuals={fetchInformedIndividuals}
          dialogTitle={intl.formatMessage({
            defaultMessage: 'Informed Individual',
          })}
          content={''}
        />
      </Stack>
    </Box>
  );
};

export default InformedIndividualsTab;
